import { useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Modal from "../../layouts/Modal";
import usePartSyncErrors from "../../data/usePartSyncErrors";
import { IPartSyncError, formatRawResponse } from "../../models/PartSyncError";
import { Marketplace, marketplaceLabel } from "../../models/Publishing";
import formatDateTime from "../../utils/formatDateTime";

/**
 * The marketplace's own reason a save failed, shown on the part rather than left in Sentry.
 *
 * A save that fails on Shopify used to surface as "Failed to update Shopify product" and nothing
 * else - the cause went to Sentry, which drops events under load. These rows are the durable copy,
 * and "View raw error" shows the bridge's response body exactly as it came back.
 */
export default function PartSyncErrorPanel({ partId }: { partId: string }) {
  const [selected, setSelected] = useState<IPartSyncError | null>(null);
  const { syncErrors } = usePartSyncErrors(partId);

  const errors = syncErrors.data ?? [];

  if (errors.length === 0) return null;

  return (
    <>
      <div className="rounded border border-red-200 bg-red-50 p-3">
        <div className="mb-2 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          <h4 className="text-sm font-semibold text-red-800">
            Marketplace {errors.length === 1 ? "error" : "errors"}
          </h4>
        </div>

        <ul className="space-y-2">
          {errors.map((err) => {
            const label = marketplaceLabel[err.marketplace as Marketplace] ?? "Marketplace";

            return (
              <li key={err.id} className="text-xs text-red-900">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold">{label}</span>
                  {err.operation && <code className="text-[10px] text-red-700">{err.operation}</code>}
                  {err.statusCode != null && (
                    <code className="text-[10px] text-red-700">HTTP {err.statusCode}</code>
                  )}
                  <span className="ml-auto text-[10px] text-red-600">
                    {formatDateTime(err.created, DateTime.DATETIME_SHORT)}
                  </span>
                </div>

                <p className="mt-0.5 break-words">{err.message}</p>

                <button
                  type="button"
                  onClick={() => setSelected(err)}
                  className="mt-0.5 text-[11px] font-medium text-primary underline"
                >
                  View raw error
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <Modal open={!!selected} setOpen={() => setSelected(null)} width="max-w-[70vw]">
        <div className="p-4">
          <h3 className="mb-1 text-lg font-semibold">
            {marketplaceLabel[selected?.marketplace as Marketplace] ?? "Marketplace"} error
          </h3>
          <p className="mb-4 text-sm text-gray-600">
            {selected?.operation}
            {selected?.statusCode != null && ` · HTTP ${selected.statusCode}`}
            {selected?.store && ` · ${selected.store}`}
            {selected?.created && ` · ${formatDateTime(selected.created, DateTime.DATETIME_SHORT)}`}
          </p>

          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            <div>
              <h4 className="mb-1 text-sm font-semibold text-gray-700">Message</h4>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border bg-gray-50 p-4 text-sm">
                {selected?.message}
              </pre>
            </div>

            <div>
              <h4 className="mb-1 text-sm font-semibold text-gray-700">Raw response</h4>
              {selected?.rawResponse ? (
                <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border bg-gray-50 p-4 text-xs">
                  {formatRawResponse(selected.rawResponse)}
                </pre>
              ) : (
                <p className="text-sm text-gray-500">
                  This failure never reached the marketplace, so there is no response body.
                </p>
              )}
            </div>

            {selected?.externalProductId && (
              <p className="text-xs text-gray-500">
                Product <code>{selected.externalProductId}</code>
              </p>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
